import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getTaskWorkTypes, getProjects, resolveRate, addManualSalaryEntry } from '../../services/api';
import { formatEur } from '../../lib/workStatus';
import { PlusCircle } from 'lucide-react';
import { toast } from 'sonner';

const pad = (n) => String(n).padStart(2, '0');

export const ManualWorkEntryForm = ({ employeeId, year, month, disabled, onSaved }) => {
  const [workTypes, setWorkTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ work_type: '', amount: '', date: '', project_id: '', note: '' });
  const [rateInfo, setRateInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const defaultDate = `${year}-${pad(month)}-01`;
  const date = form.date || defaultDate;

  useEffect(() => {
    getTaskWorkTypes().then((d) => setWorkTypes(d || [])).catch(() => {});
    getProjects().then((d) => setProjects(d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.work_type || !employeeId) { setRateInfo(null); return; }
    let cancelled = false;
    resolveRate({ work_type: form.work_type, employee_id: employeeId, date })
      .then((d) => { if (!cancelled) setRateInfo(d); })
      .catch(() => { if (!cancelled) setRateInfo(null); });
    return () => { cancelled = true; };
  }, [form.work_type, employeeId, date]);

  const selected = workTypes.find((w) => w.key === form.work_type);
  const amount = Number(form.amount || 0);
  const total = rateInfo ? amount * rateInfo.rate : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.work_type) { toast.error('Izvēlieties darba veidu'); return; }
    if (amount <= 0) { toast.error('Ievadiet apjomu'); return; }
    setSaving(true);
    try {
      const summary = await addManualSalaryEntry({
        employee_id: employeeId,
        work_type: form.work_type,
        amount,
        date: `${date}T00:00:00`,
        project_id: form.project_id || null,
        note: form.note || null
      });
      toast.success(`Pievienots: ${selected?.label} ${amount} ${selected?.unit} × ${formatEur(rateInfo?.rate)}`);
      setForm((p) => ({ ...p, amount: '', note: '' }));
      onSaved(summary);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās pievienot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm border-l-4 border-l-[#1F3D2B]" data-testid="salary-manual-entry">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><PlusCircle className="w-4 h-4" /> Pievienot darbu manuāli</CardTitle>
        <p className="text-xs text-[#2E2E2E]/60">Izvēlieties darba veidu un ievadiet apjomu — likme aizpildās automātiski (individuālā vai standarta, spēkā norādītajā datumā). Ieraksts tiek saglabāts kā apstiprināts un parādās tabulā zemāk.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end" data-testid="salary-manual-entry-form">
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Darba veids</Label>
            <Select value={form.work_type} onValueChange={(v) => setForm((p) => ({ ...p, work_type: v }))} disabled={disabled}>
              <SelectTrigger data-testid="salary-manual-work-type"><SelectValue placeholder="Izvēlieties darba veidu" /></SelectTrigger>
              <SelectContent>{workTypes.map((w) => <SelectItem key={w.key} value={w.key} data-testid={`salary-manual-work-type-${w.key}`}>{w.label} · {formatEur(w.rate)} / {w.unit}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Apjoms{selected ? ` (${selected.unit})` : ''}</Label><Input type="number" step="0.01" min="0" value={form.amount} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} data-testid="salary-manual-amount" /></div>
          <div className="space-y-1"><Label className="text-xs">Datums</Label><Input type="date" value={date} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="salary-manual-date" /></div>
          <div className="space-y-1">
            <Label className="text-xs">Objekts (nav obligāti)</Label>
            <Select value={form.project_id || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, project_id: v === 'none' ? '' : v }))} disabled={disabled}>
              <SelectTrigger data-testid="salary-manual-project"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{projects.map((pr) => <SelectItem key={pr.id} value={pr.id} data-testid={`salary-manual-project-${pr.id}`}>{pr.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={disabled || saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="salary-manual-submit">Pievienot</Button>
          <div className="space-y-1 md:col-span-3"><Label className="text-xs">Piezīme</Label><Input value={form.note} disabled={disabled} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid="salary-manual-note" /></div>
          <div className="md:col-span-3 rounded-lg bg-[#f8faf8] px-3 py-2 text-sm" data-testid="salary-manual-preview">
            {rateInfo ? (
              <>Likme: <strong data-testid="salary-manual-rate">{formatEur(rateInfo.rate)} / {rateInfo.unit}</strong> <span className="text-xs text-[#2E2E2E]/60">({rateInfo.source_label}{rateInfo.source === 'employee' && rateInfo.standard_rate != null ? `, standarta ${formatEur(rateInfo.standard_rate)}` : ''})</span>
                {amount > 0 && <> · Summa: <strong data-testid="salary-manual-total">{formatEur(total)}</strong></>}</>
            ) : <span className="text-[#2E2E2E]/60">Izvēlieties darba veidu, lai redzētu likmi.</span>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
